import { NoAvailabilitiesInfo } from '@/app/_components/widgets/listing/cardV2/NoAvailabilitiesInfo';
import { useIsDesktop } from '@/app/_hooks/useIsScreen';
import { CLIENT_ENV } from '@/config/configuration';
import { posthogTracker } from '@/utils/trackingPostHog';
import { usePostHogTrackerFeatureFlag } from '@/utils/usePostHogTrackerFeatureFlag';
import { ELButton } from '@davincihealthcare/elty-design-system-react';
import { ArrowRightIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { useCallCenterPhoneNumbers } from '@/app/_hooks/useCallCenterPhoneNumbers';

const WhatsappIcon: React.FC<React.SVGProps<SVGSVGElement>> = props => {
  return (
    <svg viewBox="0 0 21 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5859 2.90417C15.7061 1.03232 13.2059 0.000949696 10.542 0C5.05283 0 0.58547 4.44221 0.58356 9.90249C0.582605 11.648 1.04147 13.3518 1.91288 14.8533L0.5 19.9854L5.77909 18.6083C7.2335 19.3976 8.87128 19.813 10.5377 19.8135H10.542C16.0302 19.8135 20.4981 15.3708 20.5 9.91055C20.5009 7.26425 19.4662 4.7765 17.5859 2.90465V2.90417ZM10.542 18.1411H10.5387C9.05367 18.1407 7.59689 17.7436 6.32583 16.9939L6.02357 16.8154L2.89078 17.6325L3.72686 14.5949L3.53013 14.2834C2.70169 12.9729 2.26383 11.4581 2.26479 9.90298C2.2667 5.36484 5.97963 1.67241 10.5454 1.67241C12.7561 1.67336 14.8342 2.53047 16.3969 4.08655C17.9598 5.64216 18.8197 7.71061 18.8188 9.90961C18.8168 14.4482 15.1039 18.1407 10.542 18.1407V18.1411ZM15.0819 11.9766C14.8332 11.8527 13.6099 11.2544 13.3816 11.1718C13.1534 11.0891 12.9877 11.0478 12.822 11.2957C12.6563 11.5436 12.1793 12.1011 12.0342 12.2658C11.889 12.4311 11.7438 12.4515 11.4951 12.3275C11.2463 12.2036 10.4446 11.9424 9.49395 11.0996C8.75433 10.4433 8.25483 9.63326 8.10972 9.38536C7.96456 9.13751 8.09444 9.00359 8.21856 8.88061C8.33028 8.7695 8.46733 8.59144 8.59194 8.44707C8.71661 8.30271 8.75767 8.19923 8.84072 8.03442C8.92383 7.86917 8.88228 7.72486 8.82022 7.60088C8.75811 7.47696 8.26061 6.25895 8.05289 5.7637C7.85089 5.28127 7.64561 5.3468 7.49328 5.33872C7.34811 5.3316 7.18244 5.33018 7.01628 5.33018C6.85011 5.33018 6.58078 5.39191 6.35256 5.63978C6.12433 5.88762 5.48162 6.48641 5.48162 7.70392C5.48162 8.92144 6.37311 10.0986 6.49772 10.2639C6.62233 10.4291 8.25244 12.9282 10.7483 14.0004C11.3418 14.2554 11.8054 14.4078 12.1669 14.5218C12.7628 14.7103 13.3052 14.6838 13.734 14.6201C14.212 14.5489 15.2061 14.0213 15.4133 13.4434C15.6206 12.8655 15.6206 12.3698 15.5585 12.2667C15.4964 12.1637 15.3303 12.1015 15.0815 11.9776L15.0819 11.9766Z"
        fill="#189D0E"
      />
    </svg>
  );
};

const ExternalActionButtons: React.FC = () => {
  const { prefixedNumber } = useCallCenterPhoneNumbers();

  return (
    <div className="flex w-full flex-col gap-3">
      <div className="flex w-full items-center justify-center gap-2">
        <div className="h-0.5 w-8 rounded-full bg-neutral-400" />
        <span className="text-sm font-semibold text-neutral-lighter">oppure</span>
        <div className="h-0.5 w-8 rounded-full bg-neutral-400" />
      </div>

      <div className="flex items-center justify-between gap-3 [&>button:last-child]:border-[#189D0E] [&>button:last-child]:text-[#189D0E] [&>button]:flex-1">
        <ELButton
          color="primary"
          variant="outlined"
          leadingIcon={<PhoneIcon />}
          size="small"
          label="Chiama Elty"
          onClick={() => {
            window.open(`tel:${prefixedNumber}`, '_blank');
            posthogTracker.capturePhoneClick();
          }}
        />
        <ELButton
          color="primary"
          variant="outlined"
          leadingIcon={<WhatsappIcon />}
          size="small"
          label="WhatsApp"
          onClick={() => {
            if (CLIENT_ENV().NEXT_PUBLIC_ENV === 'production') {
              window.open(`https://wa.me/393203726098?text=vorrei%20prenotare%20una%20visita`, '_blank');
            } else {
              alert('WhatsApp non disponibile in questo momento');
            }
            posthogTracker.captureWhatsappClick();
          }}
        />
      </div>
    </div>
  );
};

export const BookingActions: React.FC<{
  onClick: () => void;
  showPhoneNumber?: boolean;
  onNoAvailabilitiesClick: () => void;
  hasAvailableSlots: boolean;
}> = ({ onClick, showPhoneNumber = true, hasAvailableSlots, onNoAvailabilitiesClick }) => {
  const showWhatsappButton = usePostHogTrackerFeatureFlag('show_whatsapp_button');
  const isDesktopAndBigger = useIsDesktop({ andSmaller: false });
  const { prefixedNumber, formattedNumber } = useCallCenterPhoneNumbers();

  if (!hasAvailableSlots)
    return (
      <NoAvailabilitiesInfo
        title="Al momento non ci sono orari disponibili"
        description="Vedi tutte le prestazioni"
        onClick={onNoAvailabilitiesClick}
      />
    );

  return (
    <div className="flex flex-col justify-center gap-3 md:items-center">
      <ELButton
        trailingIcon={<ArrowRightIcon />}
        color="primary"
        variant="filled"
        size={isDesktopAndBigger ? 'medium' : 'large'}
        label="Scegli giorno e ora"
        onClick={onClick}
      />

      {showPhoneNumber &&
        (!isDesktopAndBigger && showWhatsappButton === 'test' ? (
          <ExternalActionButtons />
        ) : (
          <div className="w-full self-center text-ellipsis py-3 text-center text-base font-normal text-primary lg:py-0">
            <>
              oppure chiama il{' '}
              <a
                className="whitespace-nowrap text-base font-semibold underline"
                onClick={() => {
                  if (!isDesktopAndBigger) {
                    posthogTracker.captureMobilePhoneClick();
                  }
                }}
                href={`tel:${prefixedNumber}`}
              >
                {formattedNumber}
              </a>
            </>
          </div>
        ))}
    </div>
  );
};
